/**
 * AdminCreateTestUserPage - Create test users with sample data
 *
 * Feature: 045-admin-user-tool
 * User Story 4: Create Test Users with Sample Data
 */

import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import logger from '../../utils/logger';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminGenerateTestUserGraphQL } from '../../hooks/graphql/useAdminMutationsGraphQL';
import { adminUsersUrl, adminUserDetailUrl } from '../../utils/urls';

type Preset = 'empty' | 'photographer' | 'curator';

interface PresetConfig {
  name: string;
  description: string;
  icon: string;
  photoCount: number;
  collectionCount: number;
}

const PRESETS: Record<Preset, PresetConfig> = {
  empty: {
    name: 'Empty User',
    description: 'No content - perfect for manual testing',
    icon: '👤',
    photoCount: 0,
    collectionCount: 0
  },
  photographer: {
    name: 'Active Photographer',
    description: '10-20 photos, no collections',
    icon: '📷',
    photoCount: 10,
    collectionCount: 0
  },
  curator: {
    name: 'Curator',
    description: '5 photos, 3 collections',
    icon: '🎨',
    photoCount: 5,
    collectionCount: 3
  }
};

export function AdminCreateTestUserPage() {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [selectedPreset, setSelectedPreset] = useState<Preset>('empty');
  const { generateTestUser, loading } = useAdminGenerateTestUserGraphQL();

  const handleCreate = async () => {
    try {
      const result = await generateTestUser({
        preset: selectedPreset
      });

      if (!result) {
        throw new Error('Failed to create test user');
      }

      // Refetch admin user queries via Apollo
      await apolloClient.refetchQueries({
        include: ['AdminUsers', 'AdminUserStats'],
      });

      // Navigate to created user detail page
      navigate(adminUserDetailUrl(result.user.globalId));
    } catch (error: any) {
      logger.error('AdminCreateTestUserPage', 'Failed to create test user:', error);
      alert(error.message || 'Failed to create test user');
    }
  };

  const preset = PRESETS[selectedPreset];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Create Test User</h1>
            <p className="text-muted mt-1">Quickly generate test accounts with sample data</p>
          </div>
          <Link
            to={adminUsersUrl()}
            className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
          >
            Back to Users
          </Link>
        </div>

        {/* Preset Selection */}
        <div className="bg-surface-1 rounded-lg border border-border p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Select Preset</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(Object.keys(PRESETS) as Preset[]).map((presetKey) => {
              const config = PRESETS[presetKey];
              const isSelected = selectedPreset === presetKey;

              return (
                <button
                  key={presetKey}
                  onClick={() => setSelectedPreset(presetKey)}
                  className={`p-6 rounded-lg border-2 transition-all text-left ${
                    isSelected
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50 bg-surface-2'
                  }`}
                >
                  <div className="text-4xl mb-3">{config.icon}</div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{config.name}</h3>
                  <p className="text-sm text-muted mb-3">{config.description}</p>
                  <div className="flex gap-4 text-xs text-muted">
                    <span>📸 {config.photoCount} photos</span>
                    <span>📚 {config.collectionCount} collections</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Preview & Create */}
        <div className="bg-surface-1 rounded-lg border border-border p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Preview</h2>

          <div className="bg-surface-2 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted">Type:</span>
                <span className="ml-2 text-foreground font-medium">{preset.name}</span>
              </div>
              <div>
                <span className="text-muted">Username:</span>
                <span className="ml-2 text-foreground font-mono">Auto-generated</span>
              </div>
              <div>
                <span className="text-muted">Photos:</span>
                <span className="ml-2 text-foreground">{preset.photoCount}</span>
              </div>
              <div>
                <span className="text-muted">Collections:</span>
                <span className="ml-2 text-foreground">{preset.collectionCount}</span>
              </div>
            </div>
          </div>

          {/* Creation Info */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
            <p className="text-sm text-foreground">
              <strong>Note:</strong> Test user will be created with:
            </p>
            <ul className="list-disc list-inside text-sm text-muted mt-2 space-y-1">
              <li>Email: <code className="text-xs">username@test.206mp.internal</code></li>
              <li>Password: <code className="text-xs">test123456</code></li>
              <li>Verified account (ready to use)</li>
              <li>Sample photos from Lorem Picsum (if applicable)</li>
              <li>Marked as test user (can be filtered/deleted easily)</li>
            </ul>
          </div>

          {/* Create Button */}
          <div className="flex gap-3">
            <button
              onClick={handleCreate}
              disabled={loading}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Test User...
                </span>
              ) : (
                `Create ${preset.name}`
              )}
            </button>

            {loading && (
              <div className="flex items-center text-sm text-muted">
                <span>This may take a few seconds...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
